/*
 * Back-in-stock email capture for sold-out products (Klaviyo).
 *
 * Loaded only on pages that actually render a notify form. Uses delegated listeners so cost is
 * constant regardless of how many forms are on the page, and so forms swapped in by a section
 * re-render (theme editor, variant change) keep working without re-initialisation.
 */
(function () {
  if (window.__notifyFormInitialized) return;
  window.__notifyFormInitialized = true;

  /*
   * Klaviyo's legacy onsite endpoint (/onsite/components/back-in-stock/subscribe) now returns
   * 410 Gone. The replacement is the public Client API, which is CORS-enabled and authenticates
   * with the public key as a query param.
   */
  const ENDPOINT = 'https://a.klaviyo.com/client/back-in-stock-subscriptions/';
  const API_REVISION = '2026-07-15';

  /* Klaviyo catalog variant IDs are composite: integrationType:::catalogId:::externalId */
  const VARIANT_PREFIX = '$shopify:::$default:::';

  /*
   * Klaviyo ships the onsite script under two URL shapes, and the Shopify app embed uses the
   * first one:
   *   https://static.klaviyo.com/onsite/js/PUBLIC_KEY/klaviyo.js   (key in the path)
   *   https://static.klaviyo.com/onsite/js/klaviyo.js?company_id=  (key in the query string)
   * Match on the shared prefix so both are found.
   */
  const KLAVIYO_SCRIPT_SELECTOR = 'script[src*="klaviyo.com/onsite/js"]';
  const KEY_IN_PATH = /\/onsite\/js\/([^/?#]+)\/klaviyo(?:\.min)?\.js/;

  let cachedPublicKey = '';

  function keyFromScriptTags() {
    const tags = document.querySelectorAll(KLAVIYO_SCRIPT_SELECTOR);

    for (let i = 0; i < tags.length; i++) {
      const src = tags[i].src || tags[i].getAttribute('src') || '';
      if (!src) continue;

      try {
        const url = new URL(src, window.location.href);

        const fromQuery = url.searchParams.get('company_id');
        if (fromQuery && fromQuery.trim()) return fromQuery.trim();

        const fromPath = url.pathname.match(KEY_IN_PATH);
        if (fromPath && fromPath[1]) return fromPath[1];
      } catch (e) {
        /* malformed src - try the next tag */
      }
    }

    return '';
  }

  function keyFromKlaviyoObject() {
    try {
      if (window.klaviyo && typeof window.klaviyo.account === 'function') {
        const account = window.klaviyo.account();
        if (typeof account === 'string' && account) return account;
      }
    } catch (e) {
      /* klaviyo.js still loading - not fatal */
    }

    /* Legacy _learnq queue: the account id is pushed as ['account', 'PUBLIC_KEY']. */
    try {
      const queue = window._learnq;
      if (Array.isArray(queue)) {
        for (let i = 0; i < queue.length; i++) {
          const entry = queue[i];
          if (Array.isArray(entry) && entry[0] === 'account' && typeof entry[1] === 'string' && entry[1]) {
            return entry[1];
          }
        }
      }
    } catch (e) {
      /* not present - fall through */
    }

    return '';
  }

  /*
   * Resolve the Klaviyo public API key (company ID). Klaviyo does not expose a `_klPublicApiKey`
   * global, so read the theme setting first, then fall back to sniffing the app embed's script
   * tag and the klaviyo object.
   *
   * Only successful lookups are cached: klaviyo.js is loaded async, so an early failure must not
   * be remembered or every later click on the page would inherit it.
   */
  function getKlaviyoPublicKey(form) {
    if (cachedPublicKey) return cachedPublicKey;

    const fromSetting = (form.dataset.klaviyoCompanyId || '').trim();
    cachedPublicKey = fromSetting || keyFromScriptTags() || keyFromKlaviyoObject();

    return cachedPublicKey;
  }

  function showMessage(form, text, type) {
    const message = form.querySelector('[data-notify-message]');
    if (!message) return;
    message.hidden = false;
    message.textContent = text;
    message.className = 'notify-form__message notify-form__message--' + type;
  }

  function hideMessage(form) {
    const message = form.querySelector('[data-notify-message]');
    if (message) message.hidden = true;
  }

  async function submitNotifyRequest(form) {
    const button = form.querySelector('[data-notify-button]');
    const emailInput = form.querySelector('[data-notify-email]');
    if (!button || !emailInput || button.disabled) return;

    const email = emailInput.value.trim();
    if (!email || !emailInput.checkValidity()) {
      showMessage(form, 'Please enter a valid email address.', 'error');
      emailInput.focus();
      return;
    }

    const publicKey = getKlaviyoPublicKey(form);
    const variantId = button.dataset.variantId;

    if (!publicKey || !variantId) {
      console.error(
        '[notify-form] Cannot subscribe: ' +
          (!publicKey
            ? 'no Klaviyo public API key found. Set it in Theme settings > Klaviyo, or enable the Klaviyo app embed.'
            : 'no variant ID on the notify button.')
      );
      showMessage(
        form,
        "Sorry, our notification signup is temporarily unavailable. Please email us and we'll add you to the list.",
        'error'
      );
      return;
    }

    button.classList.add('loading');
    button.disabled = true;
    hideMessage(form);

    try {
      const payload = {
        data: {
          type: 'back-in-stock-subscription',
          attributes: {
            channels: ['EMAIL'],
            profile: {
              data: {
                type: 'profile',
                attributes: { email: email },
              },
            },
          },
          relationships: {
            variant: {
              data: {
                type: 'catalog-variant',
                id: VARIANT_PREFIX + variantId,
              },
            },
          },
        },
      };

      const response = await fetch(ENDPOINT + '?company_id=' + encodeURIComponent(publicKey), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          revision: API_REVISION,
        },
        body: JSON.stringify(payload),
      });

      /* Success is 202 Accepted with an empty body. */
      if (!response.ok) {
        const body = await response.text();
        let detail = body;

        try {
          const parsed = JSON.parse(body);
          if (parsed && Array.isArray(parsed.errors) && parsed.errors.length) {
            detail = parsed.errors.map((e) => e.code + ': ' + e.detail).join('; ');
          }
        } catch (e) {
          /* keep the raw body */
        }

        throw new Error('Klaviyo responded ' + response.status + ' - ' + detail);
      }

      showMessage(form, "You're on the list! We'll let you know when we're back.", 'success');
      emailInput.value = '';
    } catch (err) {
      console.error('[notify-form] Back-in-stock subscribe failed', err);
      showMessage(form, 'Something went wrong. Please try again.', 'error');
    } finally {
      button.classList.remove('loading');
      button.disabled = false;
    }
  }

  document.addEventListener('click', function (event) {
    const target = event.target;
    if (!target || typeof target.closest !== 'function') return;

    const button = target.closest('[data-notify-button]');
    if (!button) return;

    const form = button.closest('[data-notify-form]');
    if (form) submitNotifyRequest(form);
  });

  document.addEventListener('keydown', function (event) {
    if (event.key !== 'Enter') return;

    const target = event.target;
    if (!target || typeof target.closest !== 'function') return;

    const emailInput = target.closest('[data-notify-email]');
    if (!emailInput) return;

    const form = emailInput.closest('[data-notify-form]');
    if (!form) return;

    event.preventDefault();
    submitNotifyRequest(form);
  });

  /*
   * The buy-buttons markup is not re-rendered on variant change, so keep the variant ID in sync
   * manually - otherwise we subscribe people to whichever variant was selected on page load.
   */
  if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
    subscribe(PUB_SUB_EVENTS.variantChange, function (event) {
      const data = event && event.data;
      if (!data || !data.variant || !data.variant.id) return;

      const form = document.querySelector('[data-notify-form][data-section-id="' + data.sectionId + '"]');
      if (!form) return;

      const button = form.querySelector('[data-notify-button]');
      if (!button) return;

      button.dataset.variantId = data.variant.id;
      button.dataset.variantTitle = data.variant.title || '';
      hideMessage(form);
    });
  }
})();
